import React, { useEffect, useState } from "react";
import { Mail, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DialCodePicker, flagEmoji } from "@/components/PhoneField";
import { PhoneCheckHint, usePhoneCheck } from "@/components/PhoneCheckHint";
import { EMPTY_CHECK } from "@/lib/phoneCheck";
import { detectIdentifierKind, isoFromE164, phoneFromInput, stripPhonePunctuation } from "@/lib/identifier";
import { countryLabel, dialCode } from "@/lib/countries";

// Champ unique « e-mail ou numéro WhatsApp » : l'étiquette (E-mail / WhatsApp) suit ce qui est tapé ; pour un
// numéro sans indicatif, le sélecteur de pays apparaît à gauche ; pour un numéro en +33… / 0033…, le drapeau du
// pays reconnu s'affiche en lecture seule (l'indicatif est déjà dans le numéro).
export const IdentifierField = ({ value, iso, onChange, onIsoChange, lang, labels, testId = "login-identifier", onSubmit, onCheck }) => {
  const kind = detectIdentifierKind(value);
  const intl = kind === "phone" && /^(\+|00)/.test(value.trim());
  const showPicker = kind === "phone" && !intl;
  const intlIso = intl ? isoFromE164(value) : "";
  // Lot 560 — longueur plausible pour le pays (sélectionné ou détecté) ; « trop court » affiché une fois le champ quitté.
  const parsed = kind === "phone" ? phoneFromInput(value, iso) : null;
  const check = usePhoneCheck(parsed?.candidate || "", parsed?.iso || "");
  const [focused, setFocused] = useState(false);
  useEffect(() => { onCheck?.(kind === "phone" ? check : EMPTY_CHECK); }, [check, kind, onCheck]);
  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        {showPicker && <DialCodePicker iso={iso} onSelect={onIsoChange} lang={lang} testId={testId} />}
        {intl && intlIso && (
          <span
            className="flex items-center gap-1.5 shrink-0 h-10 px-3 rounded-md border text-sm select-none"
            style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}
            title={countryLabel(intlIso, lang)}
            aria-label={countryLabel(intlIso, lang)}
            data-testid={`${testId}-detected`}
            data-iso={intlIso}
          >
            <span className="text-base leading-none">{flagEmoji(intlIso)}</span>
            <span className="font-medium">+{dialCode(intlIso)}</span>
          </span>
        )}
        <div className="relative flex-1">
          <Input
            type="text"
            inputMode={kind === "phone" ? "tel" : "email"}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={value}
            onChange={(e) => onChange(stripPhonePunctuation(e.target.value))}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => { if (e.key === "Enter" && onSubmit) { e.preventDefault(); onSubmit(); } }}
            placeholder={labels.placeholder}
            className={kind ? "pr-24" : ""}
            data-testid={testId}
          />
          {kind && (
            <span
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={kind === "phone" ? { background: "#DCFCE7", color: "#15803D" } : { background: "#EFF6FF", color: "#1D4ED8" }}
              data-testid={`${testId}-kind`}
            >
              {kind === "phone" ? <MessageCircle size={11} /> : <Mail size={11} />}
              {kind === "phone" ? labels.whatsapp : labels.email}
            </span>
          )}
        </div>
      </div>
      {kind === "phone" && <PhoneCheckHint check={check} lang={lang} testId={testId} showShort={!focused} />}
      {labels.hint ? <p className="text-[11px] leading-snug" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-hint`}>{labels.hint}</p> : null}
    </div>
  );
};
